import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Container, Spinner, Text } from "@chakra-ui/react";
import {
  ChartLineUpIcon,
  ChartScatterIcon,
  TargetIcon,
} from "@phosphor-icons/react";
import { profilingEndpoints } from "../../endpoints/profiling";
import ExerciseHeader from "./components/ExerciseHeader";
import StatsGrid from "./components/StatsGrid";
import TrainingHistoryTab from "./components/TrainingHistoryTab";
import TestingHistoryTab from "./components/TestingHistoryTab";
import ProfilingHistoryTab from "./components/ProfilingHistoryTab";
import styles from "./AthleteExerciseHistoryPage.module.scss";

/**
 * Storico Esercizi Atleta — vista con 3 sezioni IMPILATE (sez.9.2).
 * Layout: Programmazione + Testing + Profiling sempre visibili in verticale.
 * Nessun tab switcher — tutte le sezioni presenti contestualmente.
 */
export const AthleteExerciseHistoryPage: React.FC = () => {
  const { athleteId, exerciseId } = useParams<{
    athleteId: string;
    exerciseId: string;
  }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ["exercise-history", athleteId, exerciseId],
    queryFn: () =>
      profilingEndpoints
        .getExerciseHistory(athleteId!, exerciseId!, {
          limitTrainings: 50,
          limitTests: 100,
        })
        .then((r) => r.data),
    enabled: !!athleteId && !!exerciseId,
  });

  if (isLoading)
    return (
      <Container py={10}>
        <Spinner />
      </Container>
    );

  if (error || !data)
    return (
      <Container py={10}>
        <Text color="red.500">Errore caricamento storico esercizio</Text>
      </Container>
    );

  const { exercise, trainings, testResults, profiles, meta } = data;
  const exName = exercise.name?.it || exercise.name?.en || "Esercizio";

  return (
    <Container maxW="container.xl" py={6}>
      {/* Header esercizio con back link (componente modulare) */}
      <ExerciseHeader
        athleteId={athleteId!}
        exerciseId={exerciseId!}
        exerciseName={exName}
        category={exercise.category}
        recommendedForTesting={exercise.recommendedForTesting}
      />

      {/* Griglia riepilogi (componente modulare) */}
      <StatsGrid
        currentPR={meta.currentPR}
        trainingsCount={meta.trainingsCount}
        testResultsCount={meta.testResultsCount}
        profilesCount={meta.profilesCount}
      />

      {/* ── Sezioni impilate (sez.9.2) ───────────────────────────── */}
      <div className={styles.stackedSections}>

        {/* Sezione 1 — Programmazione / Storico Allenamento */}
        <div className={styles.section}>
          <div className={styles.sectionHeading}>
            <div className={styles.sectionIcon}>
              <ChartLineUpIcon size={18} weight="bold" color="#063444" />
            </div>
            <h2 className={styles.sectionTitle}>Storico Allenamento</h2>
            <span className={styles.sectionCount}>{meta.trainingsCount}</span>
          </div>
          <div className={styles.sectionBody}>
            <TrainingHistoryTab
              trainings={trainings}
              isLoading={isLoading}
            />
          </div>
        </div>

        {/* Sezione 2 — Testing: trend + ultima misurazione + cronologia */}
        <div className={styles.section}>
          <div className={styles.sectionHeading}>
            <div className={styles.sectionIcon}>
              <TargetIcon size={18} weight="bold" color="#063444" />
            </div>
            <h2 className={styles.sectionTitle}>Storico Testing</h2>
            <span className={styles.sectionCount}>{meta.testResultsCount}</span>
          </div>
          <div className={styles.sectionBody}>
            <TestingHistoryTab
              testResults={testResults}
              isLoading={isLoading}
            />
          </div>
        </div>

        {/* Sezione 3 — Profiling: SJ vs CMJ raggruppati + verdetto F-V */}
        <div className={styles.section}>
          <div className={styles.sectionHeading}>
            <div className={styles.sectionIcon}>
              <ChartScatterIcon size={18} weight="bold" color="#063444" />
            </div>
            <h2 className={styles.sectionTitle}>Storico Profiling</h2>
            <span className={styles.sectionCount}>{meta.profilesCount}</span>
          </div>
          <div className={styles.sectionBody}>
            <ProfilingHistoryTab
              profiles={profiles}
              athleteId={athleteId!}
            />
          </div>
        </div>

      </div>
    </Container>
  );
};

export default AthleteExerciseHistoryPage;
